import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { styleMap } from 'lit/directives/style-map.js';

import componentStyles from './fil-table.styles.js';

import '@fil-global/fil-button';
import '@fil-global/fil-checkbox';
import '@fil-global/fil-dropdown';
import '@fil-global/fil-icon';
import '@fil-global/fil-pagination';
import '@fil-global/fil-select';
import '@fil-global/fil-switch';
import '@fil-global/fil-text-input';
import '@fil-global/fil-tint';

/**
 * @tag fil-table
 * @property {number}[active] Active index to be passed in to pagination component
 * @property {array}[adjusted] Array of rows which are modified when using input in the row
 * @property {boolean}[basic] Use basic variation of the component
 * @property {string}[caption] Caption attribute the component
 * @property {array}[error] Rows with errors
 * @property {array}[footer] Array of footer data of the component
 * @property {array}[headers] Array of header data of the component
 * @property {boolean}[isSelectable] Whether the content can be selected
 * @property {array}[rows] Array of rows data of the component
 * @property {boolean}[scrollable] Whether the table is scrollable
 * @property {boolean}[select_all] Identifier to handle the select all functionality
 * @property {boolean}[select_all_indeterminate] Indeterminate state of the select all in the component
 * @property {'small'|'standard'}[size=standard] Size of the component
 * @property {number}[sorted_by] Index by which the data is sorted
 * @property {boolean}[sticky] Whether to use sticky table
 * @property {'small'|'standard'}[textSize=standard] Text size of the component
 * @fires click.gds.row-column-action (event: Event) => void
 */
export class FilTable extends LitElement {
  static properties = {
    active: { type: Number },
    adjusted: { type: Array },
    basic: { type: Boolean },
    caption: { type: String },
    error: { type: Array },
    footer: { type: Array },
    headers: { type: Array },
    isSelectable: { type: Boolean },
    rows: { type: Array },
    scrollable: { type: Boolean }, // if a table will transform into stacked mode beginning in medium breakpoint or not
    select_all: { type: Boolean }, // identifier to handler select all functionality.
    select_all_indeterminate: { type: Boolean },
    size: { type: String }, // 'small' | 'standard'
    sorted_by: { type: Number }, // column index by which the table will be sorted. +<index> for ascending and -<index> for descending
    sticky: { type: Boolean }, // if a scrollable table will have a sticky first column
    textSize: { type: String }, // identifier to set text size.
  };

  static styles = [componentStyles];

  constructor() {
    super();
    this.adjusted = [];
    this.basic = false;
    this.error = [];
    this.footer = [];
    this.headers = [];
    this.rows = [];
    this.isSelectable = false;
    this.scrollable = false;
    this.select_all = false;
    this.select_all_indeterminate = false;
    this.size = 'standard';
    this.sticky = false;
    this.textSize = 'standard';

    this.itemsPerPage = null;
    this.pageNumber = null;
    this.presorted = false;
    this.whiteList = ['dropdown'];
    this.inputFocusHandler = this.inputFocusHandler.bind(this);
    this.inputBlurHandler = this.inputBlurHandler.bind(this);
    this.inputChangeHandler = this.inputChangeHandler.bind(this);
    this.paginationChangeHandler = this.paginationChangeHandler.bind(this);
  }

  firstUpdated() {
    // Check for rows & headers
    if (!(this.rows && this.headers && !this.basic)) {
      throw `Invalid value for rows ${this.rows} and headers ${this.headers}`;
    }

    // row selection does not work together if 'sticky' is enabled
    if (this.sticky && this.isSelectable) {
      this.sticky = false;
    }

    // sticky works only together if 'scrollable' is enabled
    if (!this.scrollable && this.sticky) {
      this.scrollable = true;
    }
  }

  update(changedProperties) {
    if (changedProperties.has('rows') && this.sorted_by) {
      this.presorted = false;
      this.handleSort();
    }
    if (changedProperties.has('select_all') && this.isSelectable) {
      const { select_all, rows } = this;
      if (typeof select_all === 'boolean' && select_all === true) {
        rows.map(row => {
          row.selected = true;
        });
      }
    }
    super.update(changedProperties);
  }

  inputFocusHandler(row) {
    this.active = row;
  }

  inputBlurHandler() {
    this.active = null;
  }

  inputChangeHandler(row, status) {
    this.adjusted.push(row);
    if (status === 'invalid') {
      this.error.push(row);
    } else {
      this.error = this.error.filter(item => item !== row);
    }
  }

  paginationChangeHandler(event) {
    const {
      detail: {
        value: { active, itemsPerPage },
      },
    } = event;

    this.pageNumber = active;
    this.itemsPerPage = itemsPerPage;

    if (this.itemsPerPage || this.pageNumber) {
      this.requestUpdate();
    }
  }

  connectedCallback() {
    // to prevent an unnecessary sort we set 'presorted' to true if there is no 'sorted_by' set
    this.presorted = this.sorted_by === undefined;

    super.connectedCallback();
  }

  handleRowSelection(e, index) {
    this.rows[index].selected = !this.rows[index].selected;
    const selected_count = this.rows.filter(a => a.selected).length;
    this.select_all = selected_count === this.rows.length;
    this.select_all_indeterminate = !this.select_all && selected_count > 0;
    this.requestUpdate();
  }

  handleSelectAllSelection() {
    this.rows.map(row => {
      row.selected = !this.select_all;
    });
    this.select_all = !this.select_all;
  }

  handleSort(index) {
    if (index === null || index === undefined) {
      index = Math.abs(this.sorted_by || 1) - 1;
    }

    const columnDataType = this.headers[index].type;
    const columnDataFormat = this.headers[index].format;
    const sort_temp = index + 1;

    // 'sorted_by' will only be set whether we should not presort a column (this.sorted_by === undefined)
    // or presort already happened.
    if (this.sorted_by === undefined || this.presorted) {
      this.sorted_by = this.sorted_by === sort_temp ? -sort_temp : sort_temp;
    }

    const filtered_rows = this.rows
      .filter(i => i[0].type === 'parent')
      .map(j => this.rows.indexOf(j));
    if (filtered_rows.length) {
      filtered_rows.forEach((k, l) => {
        const startIndex = k;
        const lastIndex = filtered_rows[l + 1] || this.rows.length;
        this.rows = this.partialSort(
          this.rows,
          startIndex + 1,
          lastIndex,
          columnDataType,
          index,
          columnDataFormat,
        );
      });
    } else {
      this.rows = this.sortMethod(this.rows, columnDataType, index, columnDataFormat);
    }
  }

  partialSort(arr, start, end, data_type, index) {
    const preSorted = arr.slice(0, start);
    const postSorted = arr.slice(end);
    const sorted = this.sortMethod(arr.slice(start, end), data_type, index);
    arr.length = 0;
    arr.push.apply(arr, preSorted.concat(sorted).concat(postSorted));
    return arr;
  }

  sortMethod(arr, data_type, index, columnDataFormat) {
    return arr.sort((a, b) => {
      a =
        typeof a[index].value === 'string'
          ? a[index].value.toLowerCase()
          : Array.isArray(a[index])
            ? a[index].map(ele => ele.value).join(' ')
            : a[index].value.toString();
      b =
        typeof b === 'string'
          ? b[index].value.toLowerCase()
          : Array.isArray(b[index])
            ? b[index].map(ele => ele.value).join(' ')
            : b[index].value.toString();
      let result = null;
      if (data_type.toLowerCase() === 'number') {
        const aNumberArray = `${a}`.match(/\d+/gi);
        const bNumberArray = `${b}`.match(/\d+/gi);
        // deal with there is data not contains digitals
        if (!aNumberArray) {
          // the data that not contains digitals comes first
          if (this.sorted_by !== undefined && this.sorted_by < 0) {
            return -1;
          }
          return 1;
        }

        if (!bNumberArray) {
          if (this.sorted_by !== undefined && this.sorted_by < 0) {
            return 1;
          }
          return -1;
        }

        // check whether have 'format' item
        // the format should match the rules: YYYY[./-]MM[./-]DD | DD[./-]MM[./-]YYYY
        if (
          columnDataFormat &&
          columnDataFormat.match(/(Y{4}[-./]M{2}[-./]D{2}|D{2}[-./]M{2}[-./]Y{4})/gi)
        ) {
          if (aNumberArray[0].length === 4) {
            // YYYY[./-]MM[./-]DD
            a = aNumberArray.join('');
            b = bNumberArray.join('');
          } else {
            // DD[./-]MM[./-]YYYY
            a = aNumberArray.reverse().join('');
            b = bNumberArray.reverse().join('');
          }
        } else {
          a = aNumberArray.join('');
          b = bNumberArray.join('');
        }

        result =
          this.sorted_by === index + 1
            ? a.localeCompare(b, 'en', { numeric: true })
            : b.localeCompare(a, 'en', { numeric: true });
      } else {
        result = this.sorted_by === index + 1 ? a.localeCompare(b) : b.localeCompare(a);
      }
      return result;
    });
  }

  /**
   * set sticky column basic width
   */
  getColgroup() {
    return html`<colgroup>
      ${this.headers.map(item => {
        let colStyle = '';
        if (item.style) {
          colStyle = Object.keys(item.style).length === 0 ? { minWidth: '80px' } : item.style;
        } else {
          colStyle = { minWidth: '80px' };
        }
        return html`<col style=${styleMap(colStyle)} />`;
      })}
    </colgroup>`;
  }

  getHeaders() {
    // handle select_all logic
    const { rows, isSelectable } = this;
    const selectedCount = rows.filter(a => a.selected).length;
    const allSelected = isSelectable && selectedCount === rows.length;

    return html`<tr role="row">
      ${this.headers.map((item, index) => {
        const i = index + 1;
        const classes = `${item.type.toLowerCase() === 'number' ? 'text-right' : 'text-left'} ${
          item.sortable ? 'fil-table-header--sortable' : ''
        } ${this.sorted_by === i ? 'sorted-asc' : this.sorted_by === -i ? 'sorted-desc' : ''}`;

        let aria_sort = 'none';

        if (item.sortable) {
          if (this.sorted_by !== undefined && Math.abs(this.sorted_by) === i) {
            // on the first run if 'sorted_by' is set, we need to presort that column
            if (!this.presorted) {
              this.handleSort(index);
              this.presorted = true;
            }

            aria_sort = this.sorted_by > 0 ? 'ascending' : 'descending';
          }
        }

        return html`<th
          class="${ifDefined(classes)}"
          style=${ifDefined(item.style ? styleMap(item.style) : '')}
          @click="${() => (item.sortable ? this.handleSort(index) : null)}"
          @keyup="${e => {
            if (e.keyCode === 13) {
              return item.sortable ? this.handleSort(index) : null;
            }
          }}"
          tabindex="0"
          aria-sort="${aria_sort}"
          aria-label="${item.value}"
          role="columnheader"
        >
          <span class="fil-table--selectall fil-table-row-selector"
            >${index === 0 && this.isSelectable === true
              ? this.select_all || allSelected
                ? html`<fil-checkbox
                    checked
                    size="${this.textSize}"
                    @change.gds.checkbox="${this.handleSelectAllSelection}"
                  ></fil-checkbox>`
                : this.select_all_indeterminate
                  ? html`<fil-checkbox
                      ?indeterminate="${this.select_all_indeterminate}"
                      size="${this.textSize}"
                      @change.gds.checkbox="${this.handleSelectAllSelection}"
                    ></fil-checkbox>`
                  : html`<fil-checkbox
                      size="${this.textSize}"
                      @change.gds.checkbox="${this.handleSelectAllSelection}"
                    ></fil-checkbox>`
              : ''}
            ${item.sortable && item.type.toLowerCase() === 'number'
              ? html`<span class="fil-table--sort-left"
                  ><fil-icon class="fil-table--sort" size="16" icon="custom-arrow-up"></fil-icon
                ></span>`
              : null}
            ${item.value}
            ${item.sortable && item.type.toLowerCase() !== 'number'
              ? html`<span class="fil-table--sort-right"
                  ><fil-icon class="fil-table--sort" size="16" icon="custom-arrow-up"></fil-icon
                ></span>`
              : null}</span
          >
        </th>`; // 'text-left' and 'text-right' are determined based on the type of data. For numbers, we will align the value to the right.
      })}
    </tr>`;
  }

  getComponentTemplate(item, row) {
    let output = '';
    switch (item.type) {
      case 'button':
        output = html`<fil-button
          ?disabled="${item.disabled}"
          ?expand="${item.expand}"
          extraProps="${ifDefined(JSON.stringify(item.extraProps))}"
          ?fill="${item.fill}"
          icon="${ifDefined(item.icon)}"
          iconPosition="${ifDefined(item.iconPosition)}"
          iconSize="${ifDefined(item.iconSize)}"
          kind="${ifDefined(item.kind)}"
          label="${ifDefined(item.label)}"
          size="${ifDefined(this.textSize)}"
          styles="${ifDefined(JSON.stringify(item.styles))}"
          url="${ifDefined(item.url)}"
        ></fil-button>`;
        break;
      case 'code':
        output = html`<pre><code>${item.value}</code></pre>`;
        break;
      case 'dropdown':
        output = html`<fil-dropdown
          alignment="${item.alignment}"
          kind="${item.kind}"
          label="${ifDefined(item.label)}"
          options="${JSON.stringify(item.options)}"
          size="${ifDefined(this.textSize)}"
        ></fil-dropdown>`;
        break;
      case 'icon':
        output = html`<fil-icon
          size=${item.size}
          color=${item.color}
          position=${item.position}
          ?filled=${item.filled}
          icon=${item.value}
        />`;
        break;
      case 'input':
        output = html`<fil-text-input
          @focus.gds.input="${() => {
            this.inputFocusHandler(row);
          }}"
          @blur.gds.input="${() => {
            this.inputBlurHandler(row);
          }}"
          @change.gds.input="${e => {
            this.inputChangeHandler(row, e.detail.status);
          }}"
          label="${ifDefined(item.label)}"
          layout="${ifDefined(item.layout)}"
          status="${ifDefined(item.status)}"
          errortext="${ifDefined(item.errortext)}"
          mask="${item.mask}"
          assistiveText="${ifDefined(item.assistivetext)}"
          ?characterCount=${item.characterCount}
          ?required="${item.required}"
          maxlength="${ifDefined(item.maxlength)}"
          tooltip="${ifDefined(item.tooltip)}"
          size="${ifDefined(this.textSize)}"
          appearance="${this.hoveredRow === row ? 'secondary' : 'primary'}"
        ></fil-text-input>`;
        break;
      case 'label':
        output = html`<span class="text-grey">${item.value}</div>`;
        break;
      case 'link':
        output = html`<a href="${item.url}" title="${item.label}" target="${item.target || '_self'}"
          >${item.label}</a
        >`;
        break;
      case 'pagination':
        output = html`<fil-pagination
          dropdown="${JSON.stringify(item.dropdown)}"
          items="${this.rows.length}"
          @change.gds.pagination="${e => {
            this.paginationChangeHandler(e);
          }}"
          @onblur="${e => {
            this.paginationChangeHandler(e);
          }}"
          @ready.gds.pagination="${e => {
            this.paginationChangeHandler(e);
          }}"
        ></fil-pagination>`;
        break;
      case 'select':
        output = html`<fil-select
          options="${JSON.stringify(item.options)}"
          placeholder="${item.placeholderValue}"
          name="${item.name}"
          @change.gds.select="${e => {
            if (item.onChange) {
              item.onChange(e);
            }
          }}"
          size="${ifDefined(this.textSize)}"
          appearance="${this.hoveredRow === row ? 'secondary' : 'primary'}"
        ></fil-select>`;
        break;
      case 'strong':
        output = html`<strong class="fil-table--cell-title">${item.value}</strong>`;
        break;
      case 'switch':
        output = html`<fil-switch-group
          name="${ifDefined(item.name)}"
          size="${ifDefined(this.textSize)}"
        >
          ${[...item.items].map(
            (switchItem, index, { length }) => html`<fil-switch
              ?checked="${switchItem.checked}"
              ?disabled="${switchItem.disabled}"
              ?first="${index === 0}"
              icon="${ifDefined(switchItem.icon)}"
              label="${ifDefined(switchItem.label)}"
              ?last="${index + 1 === length}"
              value="${ifDefined(switchItem.value)}"
            /></fil-switch>`,
          )}</fil-switch-group
        >`;
        break;
      case 'title':
        output = html`<div class="fil-table--cell-title">${item.value}</div>`;
        break;
      case 'toggle':
        output = html`<fil-toggle
          size="${ifDefined(this.textSize)}"
          value="${item.value}"
          label="${item.label}"
        ></fil-toggle>`;
        break;
      case 'tooltip':
        output = html`<fil-tooltip
          position="${item.position}"
          alignment="${item.alignment}"
          label="${item.label}"
          message=${item.value}
          size=${item.size || 20}
          ?advanced=${item.advanced}
          ?advancedLabel=${item.advancedLabel}
          >${item.label ? item.label : ''}${item.advanced ? item.message : ''}
        </fil-tooltip>`;
        break;
      default:
        output = html`<span>${item.value}</span>`;
        break;
    }
    return output;
  }

  getRowSelector(row, i) {
    return html`${this.isSelectable === true
      ? html`
          ${row.selected
            ? html`<fil-checkbox
                checked
                size="${this.textSize}"
                @change.gds.checkbox="${e => {
                  this.handleRowSelection(e, i);
                }}"
              ></fil-checkbox>`
            : html`<fil-checkbox
                @change.gds.checkbox="${e => {
                  this.handleRowSelection(e, i);
                }}"
                size="${this.textSize}"
              ></fil-checkbox>`}
        `
      : null}`;
  }

  getMobileSort() {
    const filter_array = [];
    this.headers.forEach((item, index) => {
      if (item.sortable) {
        filter_array.push({
          label: item.value,
          index: index + 1,
        });
      }
    });
    if (filter_array.length) {
      const options = [];
      filter_array.forEach(j => {
        options.push({
          label: j.label,
          value: j.index,
          textLabel: j.label,
        });
      });

      return html`<div class="fil-table--sort-container">
        <fil-select
          class="fil-table--sort-select"
          @change.gds.select="${e => {
            this.handleSort(Math.abs(e.detail.value[0]) - 1);
          }}"
          options="${JSON.stringify(options)}"
          value="${this.sorted_by !== undefined ? `["${Math.abs(this.sorted_by)}"]` : null}"
        ></fil-select
        ><fil-button
          icon=${this.sorted_by < 0 ? 'custom-arrow-down' : 'custom-arrow-up'}
          kind="tertiary"
          @click.gds.button="${() => {
            this.handleSort();
          }}"
        ></fil-button>
      </div>`;
    }
  }

  handleToggle(row, i) {
    let endIndex = -1;
    const nextParent = this.rows.find((element, h) => {
      endIndex = h;
      return element[0].type === 'parent' && this.rows.indexOf(element) > i;
    });
    if (!nextParent) {
      endIndex = this.rows.length;
    }
    this.rows.forEach((j, k) => {
      if (k > i && k < endIndex) {
        j.show = !j.show;
      }
    });
    row.collapsed = !row.collapsed;
    this.requestUpdate();
  }

  paginate(array = this.rows, itemsPerPage = this.rows.length, pageNumber = 0) {
    return itemsPerPage
      ? [...array].slice((pageNumber - 1) * itemsPerPage, pageNumber * itemsPerPage)
      : array;
  }

  getRows(data) {
    return data.map((row, i) => {
      const cellContent = (index, item) =>
        html`${index === 0 && this.scrollable && this.sticky
            ? html`<div class="fil-table-row-selector--hover">
                ${(Array.isArray(item) ? item : [item]).map(j => {
                  if (j.type === 'label') {
                    return html`<div class="fil-table--cell-label">${j.value}</div>`;
                  }
                  return html`${j.value}`;
                })}
              </div>`
            : null}
          <div class="fil-table-row-selector">
            ${index === 0 ? this.getRowSelector(row, i) : ''}
            <div>
              ${(Array.isArray(item) ? item : [item]).map((j, k) => {
                if (j.type === 'label') {
                  return html`<div
                    class=${j.type === 'label'
                      ? `fil-table--cell-label ${
                          this.headers[k - 1]?.hideCellTypesOnSticky?.includes(j.type)
                            ? 'hide-sticky'
                            : ''
                        }`
                      : ''}
                  >
                    ${this.getComponentTemplate(j, i)}
                  </div>`;
                }
                return html`${this.getComponentTemplate(j, i)}`;
              })}
              ${index === 0 && item.type === 'input'
                ? `<span class="fil-table--error-text">${item.errortext}</span>`
                : null}
            </div>
          </div>`;

      const isParent = row[0].type === 'parent';
      const classes_row = {
        [`fil-table-row--${row.selected === true ? 'selected' : null}`]: row.selected,
        [`fil-table-row--${!row.collapsed ? 'expanded' : 'collapsed'}`]: !row.collapsed
          ? 'expanded'
          : 'collapsed',
        'fil-table-row--parent': isParent,
        'fil-hide': row.show,
      };
      const stickyTD = {
        zIndex: data.length * 20 - i,
      };

      return html`<tr
        role="row"
        class="${ifDefined(classMap(classes_row))}"
        aria-label="${isParent ? 'Section Heading' : 'Row'}"
        @mouseover=${() => {
          this.hoveredRow = i;
          this.requestUpdate();
        }}
        @mouseleave=${() => {
          this.hoveredRow = null;
          this.requestUpdate();
        }}
      >
        ${row.map((item, index) => {
          const is_multi = Array.isArray(item);
          const classes_cell = {
            'text-left': (this.headers[index].type || '').toLowerCase() !== 'number',
            'text-right': (this.headers[index].type || '').toLowerCase() === 'number',
            'fil-table-row--mobile-header': index === 0 && !this.scrollable,
            'fil-table-row--column': index !== 0,
            'fil-table-row---column-parent': isParent,
            'row-active': this.active === i && index === 0,
            'row-error': this.error.indexOf(i) > -1 && index === 0,
            'row-adjusted': this.adjusted.indexOf(i) > -1 && index === 0,
            [!is_multi ? item.className : '']: !is_multi ? item.className : '',
            'full-width': item.fullWidth,
          };
          return index === 0 && item.type === 'parent'
            ? html`<td
                role="cell"
                class="${ifDefined(classMap(classes_cell))}"
                colspan=${this.headers.length}
                tabindex="0"
                @click="${() => {
                  this.handleToggle(row, i);
                }}"
                @keyup="${e => {
                  if (e.keyCode === 13) this.handleToggle(row, i);
                }}"
              >
                ${item.collapsible
                  ? html`<fil-icon
                      size="12"
                      icon="custom-chevron-${row.collapsed ? 'down' : 'up'}"
                    ></fil-icon>`
                  : null}
                <b>${item.value}</b>
              </td>`
            : html`<td
                role="cell"
                class="${ifDefined(classMap(classes_cell))}"
                data-class="${ifDefined(item.class)}"
                data-heading="${ifDefined(this.headers[index].value)}"
                aria-label="${index === 0 && this.isSelectable ? 'Row Selector' : 'Column'}"
                style=${ifDefined(
                  index === 0 && this.scrollable && this.sticky ? styleMap(stickyTD) : undefined,
                )}
              >
                ${item.tint
                  ? html`<fil-tint color="${item.tint}">${cellContent(index, item)}</fil-tint>`
                  : html`${cellContent(index, item)}`}
              </td>`;
        })}
      </tr>`;
    });
  }

  getFooter(data) {
    return data.map(row => this.getComponentTemplate(row));
  }

  render() {
    if (this.basic) {
      // Get headers from the basic data if unavailable
      const tempHeader = this.headers.length ? this.headers : Object.keys(this.rows[0]);
      this.headers = tempHeader.map(i => ({
        value: i.value || i,
        type: isNaN(this.rows[0][i]) ? 'string' : 'number',
        sortable: i.sortable === undefined ? true : i.sortable,
        style: i.style ? i.style : {},
      }));

      // Convert Rows into format
      this.rows = this.rows.map(i => {
        const tempRow = [];
        this.headers.forEach(j => {
          tempRow.push({
            value: i[j.value],
          });
        });
        return tempRow;
      });

      this.basic = false;
    }

    const tableWrapperClasses = {
      'fil-table-wrapper': true,
      'fil-table-wrapper--scrollable': this.scrollable,
      'fil-table-wrapper--sticky-column': this.scrollable && this.sticky,
    };
    const tableClasses = {
      'fil-table': true,
      [`fil-table--${this.size}`]: this.size,
      'fil-table-body--small': this.textSize !== 'standard',
      'fil-table--responsive': !this.scrollable,
      'fil-table--fixed': this.sticky,
    };
    const tableBodyClasses = {
      'fil-table-body--sections': this.rows?.length
        ? this.rows.filter(i => i[0].type === 'parent').length
        : [],
    };

    return html`<div class="${ifDefined(classMap(tableWrapperClasses))}">
        ${!this.scrollable
          ? html`<div class="fil-table--sort-menu">${this.getMobileSort()}</div>`
          : null}
        <table class="${ifDefined(classMap(tableClasses))}" role="grid">
          ${this.caption
            ? html`<caption>
                ${this.caption}
              </caption>`
            : null}
          ${this.scrollable || this.sticky ? this.getColgroup() : null}
          <thead>
            ${this.getHeaders()}
          </thead>
          <tbody class="${ifDefined(classMap(tableBodyClasses))}">
            ${this.getRows(this.paginate(this.rows, this.itemsPerPage, this.pageNumber))}
          </tbody>
        </table>
        ${this.footer && this.footer.length && !this.scrollable
          ? html`<div class="fil-table--footer">${this.getFooter(this.footer)}</div>`
          : null}
      </div>
      ${this.footer && this.footer.length && this.scrollable
        ? html`<div class="fil-table--footer">${this.getFooter(this.footer)}</div>`
        : null} `;
  }
}

export default FilTable;
